import { useMemo } from 'react';
import { UNREACHABLE_MESSAGE } from '@/api/client';
import type { DesktopAction } from '@/components/desktop';
import { StatusPill } from '@/components/ui';
import { awaitWork, retryWork, saveWork, standingOn, takeBack } from '@/offline/engine';
import { localId, type Kind, type NewOperation, type Operation } from '@/offline/outbox';
import { pendingOps, type PendingKind } from '@/offline/pending';
import { useSavedWork } from '@/offline/useSavedWork';
import { confirmAction } from '@/stores/confirm';
import { announceSavedOffline } from '@/stores/savedOffline';

/**
 * Adding units, categories, suppliers and items with or without a line
 * (.docs/023): through the outbox, like every stock document. With a line the
 * server answers at once and gives the code; without one the entry is kept on
 * this PC, shown in every list and picker, and made when the line is back.
 *
 * Changing or deleting one still needs the line: that decides against what
 * the server holds now, which this PC cannot know.
 */

/** Said on an action that needs the server's own answer. */
export const NEEDS_THE_CONNECTION = 'Needs the connection';

export interface ReferenceSaved {
  /** The server's code, or '' while it is kept on this PC. */
  code: string;
  waiting: boolean;
}

/** A label for the dialog: "Unit “Kilogram”". */
function named(kind: PendingKind, name: string): string {
  const what = { 'uom.create': 'Unit', 'category.create': 'Category', 'supplier.create': 'Supplier', 'sku.create': 'Item' }[kind];
  return `${what} “${name}”`;
}

/**
 * Adds one entry, or several that stand on each other (an item with the new
 * category and unit typed into its form), as one batch: the last is the entry
 * itself. Refused while the form is open: taken back, and the server's words
 * thrown for the form to show.
 */
export async function saveReference(batch: readonly NewOperation[], name: string): Promise<ReferenceSaved> {
  const saved = await saveWork(batch);
  const last = saved.ops[saved.ops.length - 1];
  const code = (last?.result as { code?: string } | null)?.code ?? '';
  if (saved.outcome === 'sent') return { code, waiting: false };
  if (saved.outcome === 'waiting') {
    void announceSavedOffline({
      what: named(batch[batch.length - 1]!.kind as PendingKind, name),
      closeLabel: 'OK',
      note: 'Its code is given when it is sent. It can be chosen in Stock IN and Stock OUT straight away.',
    });
    return { code: '', waiting: true };
  }
  for (const op of saved.ops) if (op.state !== 'sent') await takeBack(op.id).catch(() => undefined);
  throw new Error(saved.ops.find((op) => op.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE);
}

/** Sends refused work again, as the form now has it. */
export async function resendReference(op: Operation, body: unknown, describes: string, name: string): Promise<ReferenceSaved> {
  const newId = await retryWork(op.id, { body, describes });
  const saved = newId ? await awaitWork([newId]) : null;
  if (!saved || saved.outcome === 'refused' || saved.outcome === 'not-kept') {
    throw new Error(saved?.ops[0]?.lastError ?? 'The server still refused it. It is kept under Offline work, Needs attention.');
  }
  if (saved.outcome === 'waiting') {
    void announceSavedOffline({ what: named(op.kind as PendingKind, name), closeLabel: 'OK' });
    return { code: '', waiting: true };
  }
  return { code: (saved.ops[0]?.result as { code?: string } | null)?.code ?? '', waiting: false };
}

/** One create through the outbox. */
export function createOp(kind: Kind, path: string, body: object, describes: string, form?: unknown): NewOperation {
  return { kind, method: 'POST', path, body, describes, screen: 'inv-reference', ...(form === undefined ? {} : { form }) };
}

/**
 * What a list in the Items Manager needs to show the entries of `kind` waiting
 * on this PC: their operations, which row is which, and what can be done
 * about one — put it right, or throw it away, once the server has refused it.
 */
export function usePendingInPanel(kind: PendingKind, onFix: (op: Operation) => void, onNotice: (message: string) => void) {
  const { data } = useSavedWork();
  const ops = useMemo(() => pendingOps(data ?? [], kind), [data, kind]);

  const opOf = (row: { id: string }) => ops.find((op) => localId(op.id) === row.id);

  async function throwAway(op: Operation) {
    const behind = await standingOn(op.id);
    const ok = await confirmAction({
      title: 'Throw this away?',
      message:
        `"${op.describes}" will be removed from this PC. The server will never see it.` +
        (behind.length ? ` These go with it, because they use it: ${behind.map((o) => o.describes).join('; ')}.` : ''),
      confirmLabel: 'Throw it away',
      cancelLabel: 'Keep it',
      danger: true,
    });
    if (!ok) return;
    await takeBack(op.id);
    onNotice('Thrown away.');
  }

  /** The actions on a pending row, in place of Edit and Delete. */
  function actionsFor(op: Operation, canFix: boolean): DesktopAction[] {
    return [
      {
        label: 'Fix it', icon: 'edit',
        onSelect: () => onFix(op),
        allowed: canFix,
        enabled: op.state === 'refused',
        reason: 'Waiting to be sent. It can be changed once the server has it.',
      },
      {
        label: 'Throw it away', icon: 'remove',
        onSelect: () => void throwAway(op),
        allowed: true,
        enabled: op.state === 'refused',
        reason: 'Work still waiting to be sent cannot be thrown away',
        separatorBefore: true,
      },
    ];
  }

  /** The Status cell of a pending row. */
  function statusOf(op: Operation) {
    return <StatusPill status={op.state === 'refused' ? 'REFUSED' : 'NOT_SENT'} />;
  }

  return { ops, opOf, actionsFor, statusOf };
}

/** Text fields of a saved body, for a form putting refused work right. */
export function bodyText(op: Operation | undefined, field: string): string {
  const value = (op?.body as Record<string, unknown> | undefined)?.[field];
  return value === undefined || value === null ? '' : String(value);
}

/**
 * Greys out what needs the server's own answer while there is no line —
 * changing or deleting an entry — with the reason on hover, rather than
 * letting it fail.
 */
export function needsTheLine(actions: DesktopAction[], online: boolean, { evenAdding = false } = {}): DesktopAction[] {
  if (online) return actions;
  return actions.map((action) =>
    action.primary && !evenAdding ? action : { ...action, enabled: false, reason: NEEDS_THE_CONNECTION },
  );
}
